import React, { useState, useEffect, useRef, useCallback } from "react";
import classNames from "classnames";
import { formatDate } from "../DateUtil";
import Calendar from "./Calendar";
import { CalendarLocalization } from "./CalendarLocalization.csharp";

export interface CalendarPickerProps {
    selectedValue: Date;
    selectedEndValue?: Date | null;
    onDateChange: (value: Date) => void;
    onRangeChange?: (range: { from: Date | undefined; to: Date | undefined }) => void;
    disabled?: boolean;
    localization: CalendarLocalization;
    dateInputPlaceholder: string;
}

const CalendarPicker: React.FC<CalendarPickerProps> = ({
    selectedValue,
    selectedEndValue,
    onDateChange,
    onRangeChange,
    disabled,
    localization,
    dateInputPlaceholder,
}) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [showOverlay, setShowOverlay] = useState(false);
    const [selectedDayInputValue, setSelectedDayInputValue] = useState("");
    const [range, setRange] = useState<{ from?: Date; to?: Date }>({});

    const handleDateChange = useCallback(
        (date: Date) => {
            onDateChange(date);
            setSelectedDayInputValue(shortenYear(formatDate(date, localization.cultureName)));
        },
        [onDateChange, localization.cultureName]
    );

    const handleRangeChange = useCallback(
        (newRange: { from: Date; to: Date }) => {
            setRange(newRange);
            if (newRange.from && newRange.to) {
                setSelectedDayInputValue(
                    `${shortenYear(formatDate(newRange.from, localization.cultureName))} - ${shortenYear(
                        formatDate(newRange.to, localization.cultureName)
                    )}`
                );
            }
            onRangeChange?.(newRange);
        },
        [onRangeChange, localization.cultureName]
    );

    const handleClickOutside = useCallback((event: MouseEvent) => {
        const node = wrapperRef.current;
        if (node && !node.contains(event.target as Node)) {
            setShowOverlay(false);
            event.preventDefault();
            event.stopPropagation();
        }
    }, []);

    const handleKeyDown = useCallback((event: KeyboardEvent | React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Escape") {
            setShowOverlay(false);
        } else if (event.key === "Enter") {
            setShowOverlay(true);
        }
    }, []);

    const shortenYear = (formattedDate: string): string => {
        // Remove the year completely, so "20.02.2025" becomes "20.02"
        return formattedDate.replace(/(\d{2})\.(\d{2})\.(\d{4})$/, (_, d, m, y) => {
            return `${d}.${m}`;
        });
    };

    const handleInputFocus = () => setShowOverlay(true);
    const handleCalendarBlur = () => setShowOverlay(false);
    const handleCloseCalendar = () => setShowOverlay(false);

    useEffect(() => {
        if (showOverlay) {
            document.addEventListener("click", handleClickOutside, true);
            document.addEventListener("keydown", handleKeyDown as EventListener);

            return () => {
                document.removeEventListener("click", handleClickOutside, true);
                document.removeEventListener("keydown", handleKeyDown as EventListener);
            };
        }
    }, [showOverlay, handleClickOutside, handleKeyDown]);

    useEffect(() => {
        if (selectedValue && selectedEndValue) {
            // If both start and end dates are provided, show as range
            setRange({ from: selectedValue, to: selectedEndValue });
            setSelectedDayInputValue(
                `${shortenYear(formatDate(selectedValue, localization.cultureName))} - ${shortenYear(
                    formatDate(selectedEndValue, localization.cultureName)
                )}`
            );
        } else if (selectedValue) {
            // If only start date is provided, show single date
            setSelectedDayInputValue(shortenYear(formatDate(selectedValue, localization.cultureName)));
            setRange({ from: selectedValue, to: undefined });
        } else {
            // Clear everything if no date is provided
            setSelectedDayInputValue("");
            setRange({ from: undefined, to: undefined });
        }
    }, [selectedValue, selectedEndValue, localization.cultureName]);

    return (
        <div className="CalendarPicker" ref={wrapperRef}>
            <div className="CalendarPicker__inputContainer">
                <input
                    className={classNames("CalendarPicker__input", {
                        "CalendarPicker__input--is-open": showOverlay,
                        "CalendarPicker__input--is-active": selectedDayInputValue !== "",
                    })}
                    value={selectedDayInputValue}
                    readOnly
                    disabled={disabled}
                    onClick={handleInputFocus}
                    onKeyDown={handleKeyDown}
                    placeholder={dateInputPlaceholder}
                    id="calendar"
                    name="calendar"
                />
            </div>
            {showOverlay && (
                <Calendar
                    selectedDate={selectedValue}
                    range={range}
                    onDateChange={handleDateChange}
                    onRangeChange={handleRangeChange}
                    onBlur={handleCalendarBlur}
                    onClose={handleCloseCalendar}
                    localization={localization}
                />
            )}
        </div>
    );
};

export default CalendarPicker;
